<script setup lang="ts">
import { useContainers } from '@/composables/rc/containers'
import { ContainerTypeSelectOptions } from '@/types/rc/containers'
import type { IContainerUpdateForm } from '@/types/rc/forms'
import type { IDamageInstance } from '@/types/rc/shipments'
import type { FormInstance, FormRules } from 'element-plus'
import { ElScrollbar } from 'element-plus'
import { reactive, ref } from 'vue'
import DamagesInputForm, { type IDamagesInputFormExposed } from './forms/DamagesInputForm.vue'

const open = defineModel<boolean>({ default: false })
const errors = ref('')

const innerRef = ref<HTMLDivElement>()
const scrollbarRef = ref<InstanceType<typeof ElScrollbar>>()

const props = defineProps<{
  containerId: number
  containerNumber: string
  containerType: string
  containerExpectedMiners: number
  damages: IDamageInstance[]
  workOrder?: string
  customer?: string
}>()

const emit = defineEmits<{
  close: [refetchContainers: boolean]
}>()

const containerFormRef = ref<FormInstance>()
const damagesFormRef = ref<IDamagesInputFormExposed>()
const damagesAccordion = ref(['damagesAccordion'])

const containerEditForm = reactive<{
  containerNumber: string
  containerType: string
  containerExpectedMiners: number | null
}>({
  containerNumber: '',
  containerType: '',
  containerExpectedMiners: null,
})

const damagesList = ref<IDamageInstance[]>([])

const initialContainerNumber = ref('')
const initialDamagesList = reactive<IDamageInstance[]>([])

const containerFormRefRules = reactive<FormRules>({
  containerType: [
    {
      required: true,
      message: 'Container type is required.',
      trigger: ['blur', 'change'],
    },
  ],
  containerNumber: [
    {
      required: true,
      message: 'Container number is required.',
      trigger: ['blur', 'change'],
    },
  ],
  containerExpectedMiners: [
    {
      required: true,
      min: 1,
      trigger: ['blur', 'change'],
      asyncValidator: async (rule: any, value: any) => {
        if (!value) {
          return Promise.reject('Number of miners is required.')
        }

        if (value && value < 1) {
          return Promise.reject('Number of miners must be greater than 0.')
        }

        return Promise.resolve()
      },
    },
  ],
})

const selectOptions = ref(ContainerTypeSelectOptions)

const handleScrollTop = () => {
  scrollbarRef.value!.setScrollTop(0)
}

const handleScrollBottom = () => {
  const max = innerRef.value!.clientHeight + 250

  setTimeout(() => {
    scrollbarRef.value!.setScrollTop(max)
  }, 0)
}

const submit = async () => {
  if (!containerFormRef.value || !damagesFormRef.value) {
    return
  }

  const containersFormValid = await containerFormRef.value.validate()
  const damagesFormValid = await damagesFormRef.value.validate()

  if (containersFormValid && damagesFormValid) {
    const newD = generateNewDamagesList()
    const deleteD = generateDeletedDamagesList()

    const updateForm: IContainerUpdateForm = {
      container_number: containerEditForm.containerNumber,
      container_type: containerEditForm.containerType,
      miners_expected: containerEditForm.containerExpectedMiners!,
      newDamages: newD,
      deletedDamages: deleteD,
    }

    await handleUpdateContainer(
      {
        id: props.containerId,
        container: updateForm,
      },
      {
        onSuccess: () => {
          resetForm(true)
        },
        onError: (e) => {
          errors.value = e.message
          handleScrollTop()
        },
      }
    )
  }
}

const { updateContainerMutation } = useContainers()
const { mutateAsync: handleUpdateContainer, isPending: isFormLoading } = updateContainerMutation

const generateNewDamagesList = (): IDamageInstance[] => {
  const newDamagesList: IDamageInstance[] = []

  damagesList.value.forEach((damage) => {
    if (!initialDamagesList.find((initialDamage) => initialDamage.id === damage.id)) {
      newDamagesList.push(damage)
    }
  })

  return newDamagesList
}

const generateDeletedDamagesList = (): IDamageInstance[] => {
  const deletedDamagesList: IDamageInstance[] = []

  initialDamagesList.forEach((initialDamage) => {
    if (!damagesList.value.find((damage) => damage.id === initialDamage.id)) {
      deletedDamagesList.push(initialDamage)
    }
  })

  return deletedDamagesList
}

const onOpen = () => {
  initialContainerNumber.value = props.containerNumber
  initialDamagesList.push(...props.damages)

  damagesList.value.push(...props.damages)
  containerEditForm.containerNumber = props.containerNumber
  containerEditForm.containerType = props.containerType
  containerEditForm.containerExpectedMiners = props.containerExpectedMiners
}

const resetForm = (refetchContainers: boolean) => {
  containerFormRef.value?.resetFields()
  damagesFormRef.value?.reset()

  errors.value = ''
  initialContainerNumber.value = ''
  initialDamagesList.splice(0)

  damagesList.value.splice(0)
  containerEditForm.containerNumber = ''
  containerEditForm.containerType = ''
  containerEditForm.containerExpectedMiners = null

  emit('close', refetchContainers)
}
</script>

<template>
  <el-dialog
    v-model="open"
    title="Edit Container"
    width="600"
    class="edit-container-dialog"
    :show-close="false"
    data-testid="edit-container-dialog"
    @open="onOpen"
    @close="() => resetForm(false)"
  >
    <el-scrollbar ref="scrollbarRef" max-height="500px">
      <div ref="innerRef" class="edit-container-dialog__content">
        <div class="edit-container-dialog__content__item">
          <div class="edit-container-dialog__content__item__row">
            <div class="edit-container-dialog__content__item__row-label">Work Order</div>
            <div v-if="props.workOrder" class="edit-container-dialog__content__item__row-value">
              {{ props.workOrder }}
            </div>
          </div>
          <div class="edit-container-dialog__content__item__row">
            <div class="edit-container-dialog__content__item__row-label">Customer</div>
            <div v-if="props.customer" class="edit-container-dialog__content__item__row-value">
              {{ props.customer }}
            </div>
          </div>
        </div>
        <div v-if="errors" class="edit-container-dialog__content__error-container">Error: {{ errors }}</div>
        <div class="edit-container-dialog__content__item">
          <div class="edit-container-dialog__content__item__label">Container</div>
          <el-form
            ref="containerFormRef"
            :disabled="isFormLoading"
            :model="containerEditForm"
            :rules="containerFormRefRules"
            class="edit-container-dialog__content__item__content"
            :scroll-into-view-options="{ behavior: 'smooth', block: 'start' }"
            scroll-to-error
          >
            <el-form-item class="edit-container-dialog__content__item__content__input" prop="containerType">
              <el-select
                v-model="containerEditForm.containerType"
                class="edit-container-dialog__content__item__content__input"
                placeholder="Select container type"
              >
                <el-option
                  v-for="option in selectOptions"
                  :key="option.key"
                  :label="option.value"
                  :value="option.value"
                />
              </el-select>
            </el-form-item>
            <el-form-item prop="containerNumber" class="edit-container-dialog__content__item__content__input">
              <el-input
                v-model="containerEditForm.containerNumber"
                class="edit-container-dialog__content__item__content__input"
                placeholder="Container Number"
                title="Container Number"
              />
            </el-form-item>
            <el-form-item prop="containerExpectedMiners" class="edit-container-dialog__content__item__content__input">
              <el-input
                v-model="containerEditForm.containerExpectedMiners"
                class="edit-container-dialog__content__item__content__input"
                placeholder="Expected Miners"
                type="number"
                min="0"
              />
            </el-form-item>
          </el-form>
        </div>
        <div class="edit-container-dialog__content__item">
          <el-collapse v-model="damagesAccordion" accordion class="edit-container-dialog__content__item__accordion">
            <el-collapse-item class="edit-container-dialog__content__item__accordion-item" name="damagesAccordion">
              <template #title>
                <div class="edit-container-dialog__content__item__header">Damages</div>
              </template>
              <DamagesInputForm
                ref="damagesFormRef"
                v-model="damagesList"
                :disabled="isFormLoading"
                @add-damages="handleScrollBottom"
              />
            </el-collapse-item>
          </el-collapse>
        </div>
      </div>
    </el-scrollbar>
    <template #footer>
      <el-button :disabled="isFormLoading" data-testid="edit-container-dialog-cancel" @click="resetForm(false)"
        >Cancel</el-button
      >
      <el-button :loading="isFormLoading" type="primary" @click="submit">Submit</el-button>
    </template>
  </el-dialog>
</template>

<style scoped lang="scss">
.edit-container-dialog {
  &__content {
    overflow-y: auto;
    padding: 0 var(--spacing-spacing-ss);

    &__error-container {
      color: var(--el-color-danger);
      margin-bottom: var(--spacing-spacing-xs);
    }

    &__item {
      padding-bottom: 25px;
      margin-bottom: 25px;
      border-bottom: 1px solid var(--fill-tertiary);

      ::v-deep(.el-collapse-item) {
        .el-collapse-item__header {
          border-bottom: none;
          font-size: 16px;
          font-weight: 600;
        }

        .el-collapse-item__wrap {
          border-bottom: none;
        }

        .el-collapse-item__content {
          padding-bottom: 0;
        }
      }

      &:last-of-type {
        margin-bottom: 0;
        border-bottom: none;
      }

      &__header {
        font-size: 14px;
        color: var(--text-component-label);
        margin-bottom: 10px;
      }

      &__content {
        display: flex;
        flex-direction: row;
        gap: var(--spacing-spacing-md);

        &__input {
          flex: 1;
        }
      }

      &__label {
        font-size: 14px;
        color: var(--text-primary);
        margin-bottom: 20px;
      }

      &__damages {
        display: flex;
        flex-direction: column;
        margin-bottom: var(--spacing-spacing-md);
      }

      &__row {
        display: flex;
        flex-direction: row;
        margin-bottom: 15px;

        &:last-of-type {
          margin-bottom: 0;
        }

        &-label {
          font-size: 14px;
          font-weight: 500;
          line-height: 20px;
          color: var(--text-tertiary);
          margin-right: 8px;
          width: 15%;
        }

        &-value {
          font-size: 14px;
          font-weight: 400;
          line-height: 20px;
          color: var(--text-primary);
        }
      }

      &__accordion {
        border: none;

        &-section {
          &__containers-list {
            &-item {
              display: flex;
              align-items: stretch;
              flex-direction: column;

              &__label {
                font-size: 14px;
                line-height: 20px;
                margin-bottom: var(--spacing-spacing-xs);
                color: var(--text-component-label);
              }

              &__container {
                display: flex;
                flex-direction: row;
                gap: var(--spacing-spacing-xs);
                margin-bottom: 15px;
              }

              &__input {
                flex: 1;
              }
            }
          }
        }
      }
    }
  }

  .damages-creation-form {
    margin-bottom: var(--spacing-spacing-md);

    &:last-of-type {
      margin-bottom: 0;
    }

    ::v-deep(.el-textarea__inner) {
      height: 146px;
    }

    &__header {
      display: flex;
      flex-direction: row;
      justify-content: space-between;
      align-items: center;

      &__label {
        font-size: 14px;
        color: var(--text-component-label);
        margin-bottom: var(--spacing-spacing-xs);
      }

      &__icon {
        cursor: pointer;
        display: none;
        color: var(--text-component-label);
      }
    }

    &:hover {
      .damages-creation-form__header__icon {
        display: block;
      }
    }

    &__add-damage {
      display: flex;
      flex-direction: row;
      gap: var(--spacing-spacing-md);

      &__img {
        border-radius: var(--spacing-spacing-xs);

        &-container {
          flex: 1;
          max-width: 240px;
        }

        &-placeholder {
          height: 146px;
          width: 238px;
          border-radius: var(--spacing-spacing-xs);
        }
      }

      &__actions {
        &-container {
          display: flex;
          align-items: center;
          cursor: pointer;
          color: var(--text-component-label);
        }
      }

      &__info {
        &-container {
          display: flex;
          flex-direction: column;
          flex: 1;
          gap: var(--spacing-spacing-xs);

          &__input {
            background: var(--component-fill-primary);
            border-radius: var(--spacing-spacing-xs);

            textarea {
              height: 90px;
            }
          }

          &__upload-img {
            width: 100%;
          }
        }
      }
    }
  }
}
</style>
